import React, { useState, useEffect } from 'react';
import NavBar from '../components/NavBar';
import api from '../api';

const FLAG = code => code ? String.fromCodePoint(...[...code.toUpperCase()].map(c => 0x1F1E6 - 65 + c.charCodeAt(0))) : '🌐';

export default function Security() {
  const [data, setData] = useState({ banned: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [total, setTotal] = useState(0);
  const [unbanning, setUnbanning] = useState(null);
  const limit = 50;

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/security/banned?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}`);
      setData(d);
      setTotal(d.total || d.banned?.length || 0);
    } catch(e) {
      console.error('Security load error:', e);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [page]);
  useEffect(() => { const t = setInterval(load, 60000); return () => clearInterval(t); }, []);
  useEffect(() => { setPage(1); load(); }, [search]);



  const handleUnban = async (ip) => {
    if (!window.confirm(`Unban ${ip}?`)) return;
    setUnbanning(ip);
    try {
      await api.delete(`/security/banned/${ip}`);
      setData(d => ({ ...d, banned: d.banned.filter(b => b.ip !== ip) }));
    } catch { alert('Unban failed'); }
    setUnbanning(null);
  };

  const filtered = data.banned || [];
  const totalPages = Math.ceil(total / limit);

  const s = {
    page: { padding: '28px 24px', maxWidth: '1100px', margin: '0 auto' },
    title: { fontSize: '24px', fontWeight: '800', marginBottom: '4px' },
    sub: { color: 'var(--text-muted)', fontSize: '14px', marginBottom: '24px' },
    stats: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '24px' },
    stat: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '12px', padding: '16px 20px' },
    statLabel: { fontSize: '11px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '6px' },
    statValue: { fontSize: '28px', fontWeight: '800', color: 'var(--text)' },
    toolbar: { display: 'flex', gap: '10px', marginBottom: '14px', alignItems: 'center' },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { textAlign: 'left', fontSize: '11px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.8px', padding: '8px 12px', borderBottom: '1px solid var(--border)' },
    td: { padding: '10px 12px', borderBottom: '1px solid var(--border)', fontSize: '13px', verticalAlign: 'middle' },
    unbanBtn: { padding: '4px 10px', borderRadius: '6px', border: '1px solid rgba(220,38,38,0.3)', background: 'var(--danger-bg)', color: 'var(--danger)', fontSize: '11px', fontWeight: '700', cursor: 'pointer' },
    pagination: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginTop: '20px' },
    pageBtn: { padding: '7px 16px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', cursor: 'pointer', fontWeight: '600', fontSize: '13px' },
  };

  return (
    <NavBar>
      <div style={s.page}>
        <div style={s.title}>🛡 Security</div>
        <div style={s.sub}>SSH brute force protection — Fail2ban</div>

        <div style={s.stats}>
          <div style={s.stat}>
            <div style={s.statLabel}>Currently Banned</div>
            <div style={{...s.statValue, color:'var(--danger)'}}>{data.stats.currentBanned || 0}</div>
          </div>
          <div style={s.stat}>
            <div style={s.statLabel}>Total Banned</div>
            <div style={s.statValue}>{data.stats.totalBanned || 0}</div>
          </div>
          <div style={s.stat}>
            <div style={s.statLabel}>Failed Attempts</div>
            <div style={s.statValue}>{data.stats.totalFailed?.toLocaleString() || 0}</div>
          </div>
        </div>

        <div style={s.toolbar}>
          <input
            placeholder="Search IP or country..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--surface2)', color: 'var(--text)', fontSize: '13px', width: '260px' }}
          />
          <span style={{ fontSize: '13px', color: 'var(--text-muted)', marginLeft: 'auto' }}>
            {total} banned IPs
          </span>
          <button onClick={load} style={{ padding: '8px 14px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', cursor: 'pointer', fontSize: '13px' }}>
            ↻ Refresh
          </button>
        </div>

        {loading ? (
          <div style={{ color: 'var(--text-muted)', padding: '32px 0' }}>Loading…</div>
        ) : (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '12px', overflow: 'hidden' }}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>IP Address</th>
                  <th style={s.th}>Country</th>
                  <th style={s.th}>City</th>
                  <th style={s.th}>ISP</th>
                  <th style={s.th}>Banned At</th>
                  <th style={s.th}>Expires</th>
                  <th style={s.th}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(b => {
                  return (
                    <tr key={b.ip} style={{ ':hover': { background: 'var(--surface2)' } }}>
                      <td style={{...s.td, fontFamily:'var(--mono)', fontWeight:'600'}}>{b.ip}</td>
                      <td style={s.td}>
                        {b.country ? <span>{FLAG(b.countryCode)} {b.country}</span> : <span style={{color:'var(--text-muted)'}}>—</span>}
                      </td>
                      <td style={{...s.td, color:'var(--text-muted)'}}>{b.city || '—'}</td>
                      <td style={{...s.td, color:'var(--text-muted)', fontSize:'12px', maxWidth:'200px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{b.isp || '—'}</td>
                      <td style={{...s.td, color:'var(--text-muted)', fontSize:'12px'}}>{b.bannedAt}</td>
                      <td style={{...s.td, color:'var(--text-muted)', fontSize:'12px'}}>{b.expiresAt}</td>
                      <td style={s.td}>
                        <button
                          style={{...s.unbanBtn, opacity: unbanning===b.ip ? 0.5 : 1}}
                          disabled={unbanning===b.ip}
                          onClick={() => handleUnban(b.ip)}
                        >
                          {unbanning===b.ip ? '…' : 'Unban'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div style={s.pagination}>
            <button style={{...s.pageBtn, opacity:page===1?0.4:1}} disabled={page===1} onClick={()=>setPage(p=>p-1)}>← Previous</button>
            <span style={{fontSize:'13px',color:'var(--text-muted)'}}>Page {page} of {totalPages}</span>
            <button style={{...s.pageBtn, opacity:page>=totalPages?0.4:1}} disabled={page>=totalPages} onClick={()=>setPage(p=>p+1)}>Next →</button>
          </div>
        )}
      </div>
    </NavBar>
  );
}
